import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { TrailerType, CargoType, TrailerQuality } from "dora-contracts";
import { Trailer } from "../../data/trailers/types";
import mapValues from "lodash/mapValues";
import * as dataActions from "../../data/trailers";

const prefix = "app/trailer-dialog";

type NullableStringToEmpty<T> = T extends null ? string : T;
type ConvertObject<T> = {
  [Key in keyof T]: NullableStringToEmpty<T[Key]>;
};

// Converts null values in an object to empty strings
export function parseNullable<T extends Record<string, any>>(
  obj: T
): ConvertObject<T> {
  return mapValues(obj, (v) => (v === null ? "" : v) as any);
}

export type FormData = {
  id?: string;
  type: TrailerType | null;
  note: string;
  lm: string;
  weight: string;
  height: string;
  width: string;
  length: string;
  volume: string | null;
  number: string;
  department: string;
  powered: boolean;
  qualities: (string | TrailerQuality | CargoType)[];
  deviationPercentage: string | null;
  fuelType: "DIESEL" | "CNG" | "LNG" | "BEV" | null;
  vehicleType:
    | "EURO 1"
    | "EURO 2"
    | "EURO 3"
    | "EURO 4"
    | "EURO 5"
    | "EURO 6"
    | "Electric"
    | "Hydrogen"
    | "Gas"
    | null;
  kmPerLiter: string | null;
  trackerDeviceId: string | null;
  departmentId: string | null;
  alias: string | null;
  color: string | null;
  trailerWeight: string | null;
  co2EmissionsClass:
    | "CLASS 1"
    | "CLASS 2"
    | "CLASS 3"
    | "CLASS 4"
    | "CLASS 5"
    | null;
};

type State = {
  open: boolean;
  initialFormData: FormData | null;
  draftPowered: FormData | null;
  draftNonPowered: FormData | null;
};

const initialState: State = {
  open: false,
  initialFormData: null,
  draftPowered: null,
  draftNonPowered: null,
};

export const getEmptyFormData = (powered: boolean): FormData => ({
  powered,
  number: "",
  type: null,
  note: "",
  lm: "",
  weight: "",
  height: "",
  width: "",
  length: "",
  volume: "",
  department: "",
  qualities: [],
  deviationPercentage: null,
  fuelType: null,
  vehicleType: null,
  kmPerLiter: null,
  trackerDeviceId: null,
  departmentId: null,
  alias: null,
  color: null,
  trailerWeight: null,
  co2EmissionsClass: null,
});

const slice = createSlice({
  name: prefix,
  initialState,
  reducers: {
    addTrailer(state, action: PayloadAction<{ powered: boolean }>) {
      const { powered } = action.payload;
      const draft = powered ? state.draftPowered : state.draftNonPowered;
      state.open = true;
      state.initialFormData = draft || getEmptyFormData(powered);
    },
    editTrailer(state, action: PayloadAction<Trailer>) {
      state.open = true;
      state.initialFormData = {
        ...parseNullable(action.payload),
        fuelType: action.payload.fuelType,
        vehicleType: action.payload.vehicleType,
        deviationPercentage: action.payload.deviationPercentage,
        kmPerLiter: action.payload.kmPerLiter,
        qualities: action.payload.qualities || [],
        departmentId: action.payload.departmentId,
        trailerWeight: action.payload.trailerWeight,
        co2EmissionsClass: action.payload.co2EmissionsClass,
      };
    },
    setTrailer(state, action: PayloadAction<FormData>) {
      state.open = true;
      state.initialFormData = action.payload;
    },
    close(state) {
      state.open = false;
      state.initialFormData = null;
    },
    closeWithDraft(state, action: PayloadAction<FormData>) {
      state.open = false;
      if (action.payload.powered) {
        state.draftPowered = action.payload;
      } else {
        state.draftNonPowered = action.payload;
      }
      state.initialFormData = null;
    },
    clearDraft(state) {
      if (state.initialFormData?.powered) {
        state.draftPowered = null;
      } else {
        state.draftNonPowered = null;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(dataActions.addTrailer.fulfilled, (state) => {
      if (state.initialFormData?.powered) {
        state.draftPowered = null;
      } else {
        state.draftNonPowered = null;
      }
      state.open = false;
      state.initialFormData = null;
    });
    builder.addCase(dataActions.updateTrailer.fulfilled, (state) => {
      state.open = false;
      state.initialFormData = null;
    });
  },
});

export default slice.reducer;
export const { addTrailer, editTrailer, close, closeWithDraft, clearDraft } =
  slice.actions;
